import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SearchCriterion, SearchableField } from './CommonTypes';
import SearchExportBar from './SearchExportBar';
import './SearchListBar.css';

// Component props
export interface SearchListBarProps {
  // Configuration
  title?: string;
  searchableFields: SearchableField[];
  
  // State
  searchValue: string;              // Current input value
  selectedCriteria: SearchCriterion[]; // Multiple active criteria
  
  // Callbacks
  onSearchChange: (value: string) => void;
  onCriteriaChange: (criteria: SearchCriterion[]) => void;
  
  // Optional
  className?: string;
  placeholder?: string;
  
  // Export - when provided, shows download icon to the right of search bar
  onExport?: () => void | Promise<void>;
  exportLoading?: boolean;
}

const SearchListBar: React.FC<SearchListBarProps> = ({
  title,
  searchableFields,
  searchValue,
  selectedCriteria,
  onSearchChange,
  onCriteriaChange,
  className = '',
  placeholder = 'Search...',
  onExport,
  exportLoading = false
}) => {
  const [showFieldMenu, setShowFieldMenu] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const updateMenuPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Use CSS custom properties for positioning
      document.documentElement.style.setProperty('--search-menu-top', `${rect.bottom + window.scrollY + 4}px`);
      document.documentElement.style.setProperty('--search-menu-left', `${rect.left + window.scrollX}px`);
    }
  };

  const handleToggleMenu = () => {
    if (!showFieldMenu) {
      updateMenuPosition();
    }
    setShowFieldMenu(!showFieldMenu);
  };

  // Update menu position on scroll or resize
  useEffect(() => {
    if (showFieldMenu) {
      const handleScrollOrResize = () => {
        updateMenuPosition();
      };
      
      window.addEventListener('scroll', handleScrollOrResize);
      window.addEventListener('resize', handleScrollOrResize);
      
      return () => {
        window.removeEventListener('scroll', handleScrollOrResize);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }
  }, [showFieldMenu]);

  const handleFieldSelect = (field: SearchableField) => {
    // Add new criterion to the list
    const newCriterion: SearchCriterion = {
      field: field.key,
      label: field.label,
      type: field.type
    };
    
    onCriteriaChange([...selectedCriteria, newCriterion]);
    setShowFieldMenu(false);
  };

  const handleRemoveCriterion = (fieldKey: string) => {
    onCriteriaChange(selectedCriteria.filter(c => c.field !== fieldKey));
  };

  const handleSearchChange = (value: string) => {
    onSearchChange(value);
  };

  const handleClearAll = () => {
    onSearchChange('');
    onCriteriaChange([]);
  };

  // Filter out already selected fields
  const getAvailableFields = () => {
    const selectedFields = selectedCriteria.map(c => c.field);
    return searchableFields.filter(field => !selectedFields.includes(field.key));
  };

  return (
    <div className={`search-list-bar ${className}`}>
      {/* Search input with + button */}
      <div className="search-list-bar-input-group">
        <input
          type="text"
          className="search-list-bar-input"
          value={searchValue}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder={selectedCriteria.length > 0
            ? `Searching ${selectedCriteria.map(c => c.label).join(', ')}...` 
            : placeholder}
          aria-label={selectedCriteria.length > 0
            ? `Search by ${selectedCriteria.map(c => c.label).join(', ')}` 
            : 'Search all fields'}
        />
        
        <button
          ref={buttonRef}
          type="button"
          className="app-btn app-btn-primary app-btn-small"
          onClick={handleToggleMenu}
          disabled={getAvailableFields().length === 0}
          title="Select search field"
          aria-label="Select search field"
        >
          +
        </button>
        
        {onExport && (
          <SearchExportBar
            onExport={onExport}
            loading={exportLoading}
            title="Export search results as CSV"
          />
        )}
        
        {/* Clear button - only show when there's content to clear */}
        {(searchValue.trim() !== '' || selectedCriteria.length > 0) && (
          <button
            type="button"
            className="search-list-bar-clear-button"
            onClick={handleClearAll}
            title="Clear search and filters"
            aria-label="Clear search and filters"
          >
            ✕
          </button>
        )}
        
        {showFieldMenu && createPortal(
          <div className="search-list-bar-field-menu search-list-bar-field-menu-positioned">
            {getAvailableFields().map(field => (
              <button
                key={field.key}
                type="button"
                className="search-list-bar-field-option"
                onClick={() => handleFieldSelect(field)}
              >
                {field.label}
              </button>
            ))}
          </div>,
          document.body
        )}
      </div>

      {/* Search chips below the input (if any fields selected) */}
      {selectedCriteria.length > 0 && (
        <div className="search-list-bar-chips">
          {selectedCriteria.map((criterion) => (
            <div key={criterion.field} className="search-list-bar-chip">
              <span className="search-chip-label">{criterion.label}</span>
              <button
                type="button"
                className="search-chip-remove-button"
                onClick={() => handleRemoveCriterion(criterion.field)}
                title="Remove field filter"
                aria-label={`Remove ${criterion.label} filter`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchListBar;
